import { enUS, es, fr, uk } from 'date-fns/locale'
import { computed, observable, runInAction } from 'mobx'

import Config from '~/client/src/shared/Config'
import { enumToList } from '~/client/src/shared/utils/converters'
import { safeGetItem, safeSetItem } from '~/client/src/shared/utils/safeStorage'

import EnglishTree from './english/tree'
import FrenchTree from './french/tree'
import KnownTranslatorKeys from './knownTranslatorKeys'
import Language from './Language'
import SpanishTree from './spanish/tree'
import UkrainianTree from './ukrainian/tree'

const defaultLanguage = Language.english

const STORAGE_KEY = 'language'
interface ILocalization {
  currentLanguage: Language
}

export default class Localization {
  public static state: ILocalization = observable({
    currentLanguage: defaultLanguage,
  })

  @computed
  public static get translator() {
    switch (Localization.state.currentLanguage) {
      case Language.english:
        return EnglishTree

      case Language.ukrainian:
        return UkrainianTree

      case Language.spanish:
        return SpanishTree

      case Language.french:
        return FrenchTree

      default:
        return EnglishTree
    }
  }

  public static get dateLocale() {
    switch (Localization.state.currentLanguage) {
      case Language.english:
        return enUS

      case Language.ukrainian:
        return uk

      case Language.spanish:
        return es

      case Language.french:
        return fr

      default:
        return enUS
    }
  }

  public static init() {
    // Runs before the app renders, so a throw here is a blank page rather
    // than a degraded feature. A browser that denies storage falls back to
    // the default language; the preference simply is not remembered.
    const language = safeGetItem(STORAGE_KEY) as Language
    runInAction(() => {
      Localization.state.currentLanguage = language || defaultLanguage
    })
  }

  public static setLanguage(language: Language) {
    // Guards only the observable write: federated components re-assert the host locale on every
    // mount, and a no-op sync would otherwise invalidate every observer of currentLanguage. The
    // preference is still recorded, so an explicit pick of the current language is not lost.
    if (Localization.state.currentLanguage !== language) {
      runInAction(() => {
        Localization.state.currentLanguage = language
      })
    }

    safeSetItem(STORAGE_KEY, language)
  }

  public static getLanguageOptions(): Language[] {
    const options = enumToList(Language)

    // Absent on login hosts — they skip the /configuration fetch (Config.ts).
    // Unknown is not development, so Ukrainian stays filtered.
    if ((Config.ENVIRONMENT || '').toLowerCase() === 'development') {
      return options
    }

    // We should not show Ukrainian in the QA, PROD environments for now according to thr Chris directions
    return options.filter(l => l !== Language.ukrainian)
  }

  public static getText(path: KnownTranslatorKeys, ...args: any[]): string {
    if (!path) {
      return
    }

    const subpaths = path.split('.')
    let value: any = Localization.translator
    do {
      value = value[subpaths.shift()]
      if (!value) {
        throw new Error('No such string in language tree: ' + path)
      }
    } while (subpaths.length)

    if (typeof value === 'function') {
      return value(...args)
    }

    return value
  }

  public static get shouldShowLanguageSelector() {
    // TODO: provide logic to hide it from AdminTool for some specific tenant
    return true
  }
}
